import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ProgressBar = ({ progress, isComplete }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <motion.div
            animate={{
              rotate: isComplete ? 0 : 360,
              scale: isComplete ? 1.1 : 1
            }}
            transition={{
              rotate: {
                duration: 2,
                repeat: isComplete ? 0 : Infinity,
                ease: "linear"
              },
              scale: {
                duration: 0.3
              }
            }}
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isComplete ? 'bg-green-100' : 'bg-purple-100'
            }`}
          >
            <Icon 
              name={isComplete ? "CheckCircle2" : "Sparkles"} 
              size={32} 
              color={isComplete ? "#10B981" : "#9333EA"} 
            />
          </motion.div>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-2">
          {isComplete ? 'Analysis Complete!' : 'Analyzing Your Photos'}
        </h3>
        
        {/* Subtitle */}
        <p className="text-center text-gray-600 mb-6">
          {isComplete 
            ? 'Preparing your results...' :'Our AI is evaluating your photos'}
        </p>

        {/* Progress Bar */}
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-full"
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          
          {/* Shimmer effect */}
          {!isComplete && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              animate={{
                x: ['-100%', '200%']
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          )}
        </div>

        {/* Percentage */}
        <div className="text-center">
          <motion.span
            key={progress}
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-3xl font-bold text-purple-600"
          >
            {progress}%
          </motion.span>
        </div>

        {/* Processing Steps */}
        {!isComplete && (
          <div className="mt-6 space-y-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: progress >= 25 ? 1 : 0.3, x: 0 }}
              className="flex items-center gap-2 text-sm"
            >
              <Icon 
                name={progress >= 25 ? "CheckCircle2" : "Circle"} 
                size={16} 
                color={progress >= 25 ? "#10B981" : "#D1D5DB"} 
              />
              <span className={progress >= 25 ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                Uploading photos
              </span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: progress >= 50 ? 1 : 0.3, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-2 text-sm"
            >
              <Icon 
                name={progress >= 50 ? "CheckCircle2" : "Circle"} 
                size={16} 
                color={progress >= 50 ? "#10B981" : "#D1D5DB"} 
              />
              <span className={progress >= 50 ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                AI analysis in progress
              </span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: progress >= 75 ? 1 : 0.3, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-2 text-sm"
            >
              <Icon 
                name={progress >= 75 ? "CheckCircle2" : "Circle"} 
                size={16} 
                color={progress >= 75 ? "#10B981" : "#D1D5DB"} 
              />
              <span className={progress >= 75 ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                Generating rankings
              </span>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ProgressBar;